import { StorageAdapter, UserRecord, ApiKeyRecord, SessionRecord, OrganizationRecord, EmailVerificationToken, CreateUserInput, UpdateUserInput, OAuthLink, AuthEvent } from '../types'
import { nanoid } from 'nanoid'

/**
 * In-Memory Storage Adapter for Development and Testing
 *
 * ⚠️  WARNING: Data is lost on process restart.
 * DO NOT USE IN PRODUCTION.
 *
 * Perfect for:
 * - Unit testing
 * - Integration testing
 * - Development/prototyping
 * - Quick demos
 */
export class MemoryStorageAdapter implements StorageAdapter {
  private users = new Map<string, UserRecord>()
  private apiKeys = new Map<string, ApiKeyRecord>()
  private sessions = new Map<string, SessionRecord>()
  private organizations = new Map<string, OrganizationRecord>()
  private emailTokens = new Map<string, EmailVerificationToken>()
  private emailTokenAttempts = new Map<string, number>()
  private events: AuthEvent[] = []

  async connect(): Promise<void> {
    console.log('[MemoryStorageAdapter] Connected - ⚠️  Data will be lost on restart')
  }

  async disconnect(): Promise<void> {
    this.users.clear()
    this.apiKeys.clear()
    this.sessions.clear()
    this.organizations.clear()
    this.emailTokens.clear()
    this.emailTokenAttempts.clear()
    this.events = []
    console.log('[MemoryStorageAdapter] Disconnected')
  }

  // ============ API Keys ============

  async createApiKey(data: Omit<ApiKeyRecord, 'id' | 'createdAt'>): Promise<ApiKeyRecord> {
    const apiKey: ApiKeyRecord = {
      id: nanoid(),
      ...data,
      createdAt: new Date(),
      updatedAt: new Date()
    }
    this.apiKeys.set(apiKey.id, apiKey)
    return apiKey
  }

  async getApiKey(id: string): Promise<ApiKeyRecord | null> {
    return this.apiKeys.get(id) || null
  }

  async getApiKeyByHash(hash: string): Promise<ApiKeyRecord | null> {
    for (const key of this.apiKeys.values()) {
      if (key.hash === hash) return key
    }
    return null
  }

  async getApiKeysByPrefixAndLastFour(prefix: string, lastFour: string): Promise<ApiKeyRecord[]> {
    return Array.from(this.apiKeys.values()).filter(k => k.prefix === prefix && k.lastFour === lastFour)
  }

  async updateApiKey(id: string, data: Partial<ApiKeyRecord>): Promise<ApiKeyRecord> {
    const key = this.apiKeys.get(id)
    if (!key) throw new Error(`API key ${id} not found`)

    const updated = { ...key, ...data, updatedAt: new Date() }
    this.apiKeys.set(id, updated)
    return updated
  }

  async deleteApiKey(id: string): Promise<void> {
    this.apiKeys.delete(id)
  }

  async listApiKeys(principalId: string): Promise<ApiKeyRecord[]> {
    return Array.from(this.apiKeys.values()).filter(k => k.principalId === principalId)
  }

  // ============ Sessions ============

  async createSession(data: Omit<SessionRecord, 'id' | 'createdAt'>): Promise<SessionRecord> {
    const session: SessionRecord = {
      id: nanoid(),
      ...data,
      createdAt: new Date(),
      updatedAt: new Date()
    }
    this.sessions.set(session.id, session)
    return session
  }

  async getSession(id: string): Promise<SessionRecord | null> {
    const session = this.sessions.get(id)
    if (!session) return null

    // Check expiration
    if (new Date() > session.expiresAt) {
      this.sessions.delete(id)
      return null
    }

    return session
  }

  async updateSession(id: string, data: Partial<SessionRecord>): Promise<SessionRecord> {
    const session = this.sessions.get(id)
    if (!session) throw new Error(`Session ${id} not found`)

    const updated = { ...session, ...data, updatedAt: new Date() }
    this.sessions.set(id, updated)
    return updated
  }

  async deleteSession(id: string): Promise<void> {
    this.sessions.delete(id)
  }

  // ============ Organizations ============

  async createOrganization(data: Omit<OrganizationRecord, 'id' | 'createdAt' | 'updatedAt'>): Promise<OrganizationRecord> {
    const org: OrganizationRecord = {
      id: nanoid(),
      ...data,
      createdAt: new Date(),
      updatedAt: new Date()
    }
    this.organizations.set(org.id, org)
    return org
  }

  async getOrganization(id: string): Promise<OrganizationRecord | null> {
    return this.organizations.get(id) || null
  }

  async updateOrganization(id: string, data: Partial<OrganizationRecord>): Promise<OrganizationRecord> {
    const org = this.organizations.get(id)
    if (!org) throw new Error(`Organization ${id} not found`)

    const updated = { ...org, ...data, updatedAt: new Date() }
    this.organizations.set(id, updated)
    return updated
  }

  async deleteOrganization(id: string): Promise<void> {
    this.organizations.delete(id)
  }

  // ============ Users ============

  async createUser(data: CreateUserInput): Promise<UserRecord> {
    const user: UserRecord = {
      id: nanoid(),
      email: data.email,
      name: data.name,
      picture: data.picture,
      plan: data.plan || 'free',
      entitlements: data.entitlements || ['api.read'],
      oauth: {},
      metadata: data.metadata,
      createdAt: new Date(),
      updatedAt: new Date()
    }
    this.users.set(user.id, user)
    return user
  }

  async getUser(id: string): Promise<UserRecord | null> {
    return this.users.get(id) || null
  }

  async getUserByEmail(email: string): Promise<UserRecord | null> {
    for (const user of this.users.values()) {
      if (user.email === email) return user
    }
    return null
  }

  async getUserByOAuth(provider: string, sub: string): Promise<UserRecord | null> {
    for (const user of this.users.values()) {
      if (user.oauth?.[provider]?.sub === sub) return user
    }
    return null
  }

  async updateUser(id: string, data: UpdateUserInput): Promise<UserRecord> {
    const user = this.users.get(id)
    if (!user) throw new Error(`User ${id} not found`)

    const updated = { ...user, ...data, updatedAt: new Date() }
    this.users.set(id, updated)
    return updated
  }

  async deleteUser(id: string): Promise<void> {
    this.users.delete(id)
  }

  async linkOAuthAccount(userId: string, provider: string, oauthLink: OAuthLink): Promise<UserRecord> {
    const user = this.users.get(userId)
    if (!user) throw new Error(`User ${userId} not found`)

    user.oauth = user.oauth || {}
    user.oauth[provider] = oauthLink
    user.updatedAt = new Date()

    this.users.set(userId, user)
    return user
  }

  // ============ Email Verification Tokens ============

  async createEmailVerificationToken(data: Omit<EmailVerificationToken, 'id'>): Promise<EmailVerificationToken> {
    const token: EmailVerificationToken = {
      id: nanoid(),
      ...data
    }
    this.emailTokens.set(token.id, token)
    return token
  }

  async getEmailVerificationTokens(email: string, type?: string): Promise<EmailVerificationToken[]> {
    return Array.from(this.emailTokens.values()).filter(t => t.email === email && (!type || t.type === type))
  }

  async getEmailVerificationTokenById(id: string): Promise<EmailVerificationToken | null> {
    return this.emailTokens.get(id) || null
  }

  async markEmailVerificationTokenAsUsed(id: string): Promise<EmailVerificationToken> {
    const token = this.emailTokens.get(id)
    if (!token) throw new Error(`Token ${id} not found`)

    token.usedAt = new Date()
    this.emailTokens.set(id, token)
    return token
  }

  async deleteExpiredEmailVerificationTokens(): Promise<number> {
    const now = new Date()
    let count = 0

    for (const [id, token] of this.emailTokens.entries()) {
      if (token.expiresAt < now) {
        this.emailTokens.delete(id)
        count++
      }
    }

    return count
  }

  async getEmailVerificationTokenAttempts(tokenId: string): Promise<number> {
    return this.emailTokenAttempts.get(tokenId) || 0
  }

  async incrementEmailVerificationTokenAttempts(tokenId: string): Promise<number> {
    const attempts = (this.emailTokenAttempts.get(tokenId) || 0) + 1
    this.emailTokenAttempts.set(tokenId, attempts)
    return attempts
  }

  // ============ Events ============

  async emitEvent(event: AuthEvent): Promise<void> {
    this.events.push(event)
    if (this.events.length > 1000) {
      // Keep only last 1000 events to avoid memory overflow
      this.events = this.events.slice(-1000)
    }
  }

  // ============ Utility Methods for Testing ============

  /**
   * Clear all data (useful for test cleanup)
   */
  public clear(): void {
    this.users.clear()
    this.apiKeys.clear()
    this.sessions.clear()
    this.organizations.clear()
    this.emailTokens.clear()
    this.emailTokenAttempts.clear()
    this.events = []
  }

  /**
   * Get event history
   */
  public getEvents(): AuthEvent[] {
    return [...this.events]
  }

  /**
   * Get stats for debugging
   */
  public getStats() {
    return {
      users: this.users.size,
      apiKeys: this.apiKeys.size,
      sessions: this.sessions.size,
      organizations: this.organizations.size,
      emailTokens: this.emailTokens.size,
      events: this.events.length
    }
  }
}
